import styled from "@emotion/styled";
import { motion, AnimatePresence } from "motion/react";
import { Popover, PopoverProps } from "react-aria-components";
import { ZIndex } from "@/domains/design/constants";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons";

type PointCorner = "top-right" | "top-left" | "bottom-right" | "bottom-left";

interface OnboardingTooltipProps {
  title: string;
  subtitle: string;
  triggerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onDismiss?: () => void;
  // Positioning props from React Aria Popover
  placement?: PopoverProps["placement"];
  offset?: PopoverProps["offset"];
  crossOffset?: PopoverProps["crossOffset"];
  shouldFlip?: PopoverProps["shouldFlip"];
  containerPadding?: PopoverProps["containerPadding"];
  pointCorner?: PointCorner;
  width?: string | number;
  appearanceDelay?: number;
}

export const OnboardingTooltip = ({
  title,
  subtitle,
  triggerRef,
  isOpen,
  onOpenChange,
  onDismiss,
  placement,
  offset,
  crossOffset,
  shouldFlip,
  containerPadding,
  pointCorner,
  width,
  appearanceDelay = 0.3,
}: OnboardingTooltipProps) => {
  const animationX = 10;

  return (
    <Popover
      triggerRef={triggerRef}
      placement={placement}
      offset={offset}
      crossOffset={crossOffset}
      shouldFlip={shouldFlip}
      containerPadding={containerPadding}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isNonModal
      style={{ zIndex: ZIndex.FloatingTooltip }}
    >
      <AnimatePresence>
        {isOpen && (
          <StyledOnboardingTooltip
            initial={{ opacity: 0, x: animationX }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: animationX }}
            transition={{ delay: appearanceDelay, duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
            $pointCorner={pointCorner ?? "top-left"}
            $width={width}
          >
            <TooltipContent>
              <TooltipTitle>{title}</TooltipTitle>
              <TooltipSubtitle>{subtitle}</TooltipSubtitle>
              {onDismiss && (
                <GotItContainer>
                  <MDSButton variant={MDSButtonVariant.ContrastAccent} onPress={onDismiss}>
                    Got it
                  </MDSButton>
                </GotItContainer>
              )}
            </TooltipContent>
          </StyledOnboardingTooltip>
        )}
      </AnimatePresence>
    </Popover>
  );
};

const StyledOnboardingTooltip = styled(motion.div)<{
  $pointCorner: PointCorner;
  $width?: string | number;
}>`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.surface.tooltip};
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.large} ${theme.borderRadius.large} ${theme.borderRadius.large} ${theme.borderRadius.large}`};
  border-top-left-radius: ${({ $pointCorner }) => $pointCorner === "top-left" && 0};
  border-top-right-radius: ${({ $pointCorner }) => $pointCorner === "top-right" && 0};
  border-bottom-left-radius: ${({ $pointCorner }) => $pointCorner === "bottom-left" && 0};
  border-bottom-right-radius: ${({ $pointCorner }) => $pointCorner === "bottom-right" && 0};
  padding: ${({ theme }) => theme.spacing[4]};
  max-width: ${({ $width }) =>
    $width ? (typeof $width === "number" ? `${$width}px` : $width) : "164px"};
  box-shadow: ${({ theme }) => theme.shadows.surface};
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const TooltipTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.onAccent};
  font-size: ${({ theme }) => theme.fontSizes["header-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
  line-height: ${({ theme }) => theme.lineHeights["header-s"]};
`;

const TooltipSubtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.onAccent};
  font-size: ${({ theme }) => theme.fontSizes["text-sm"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
`;

const GotItContainer = styled.div`
  width: 100px;
`;
